import { useState } from "react";
import { Check, X } from "lucide-react";
import type { Holiday, HolidaysResponse } from "@opus-dt/shared-types";
import type { UseQueryResult } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Notify } from "@/components/ui/notify";

interface Props {
  year: number;
  query: UseQueryResult<HolidaysResponse>;
  /** Datas que o calendário já tem — exibidas, mas fora da seleção. */
  alreadyPresent: Set<string>;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (chosen: Holiday[]) => void;
}

const fmt = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  weekday: "short",
  timeZone: "UTC",
});

// A REVISÃO que precede a gravação.
// O catálogo é sugestão, e a planta decide: feriado nacional que a fábrica opera existe, e
// segunda de Carnaval que ela para também. Emendar direto no calendário economizaria um clique e
// tiraria a decisão de quem a tem.
// A segunda de Carnaval chega `suggestion: true` e nasce DESMARCADA — sugerir é útil, decidir pelo
// gestor não é.
export function HolidayReview({ year, query, alreadyPresent, busy, onCancel, onConfirm }: Props) {
  // O estado guarda só o que o gestor MEXEU, e a marcação é DERIVADA dele mais o default da data.
  // A alternativa — semear "desmarcados" por efeito quando a query resolve — precisa de um
  // `setState` dentro de `useEffect`, que é cascata de render e o que o lint do React Compiler
  // acusa. Derivar também não tem o bug de sincronia: trocar de ano não deixa a seleção do ano
  // anterior grudada.
  const [toggled, setToggled] = useState<Set<string>>(new Set());

  /** Default: feriado nacional vem marcado; sugestão (segunda de Carnaval) vem desmarcada. */
  const isChecked = (h: Holiday): boolean => (toggled.has(h.date) ? h.suggestion : !h.suggestion);

  if (query.isError) {
    return (
      <Notify.QueryError
        error={query.error}
        onRetry={() => void query.refetch()}
        title={`Não foi possível carregar o catálogo de ${year}`}
        variant="inline"
      />
    );
  }
  if (query.isLoading || !query.data) {
    return <p className="text-[12px] text-text-secondary">Carregando feriados de {year}…</p>;
  }

  const holidays = query.data.holidays;
  const novos = holidays.filter((h) => !alreadyPresent.has(h.date));
  const chosen = novos.filter(isChecked);

  const toggle = (date: string) =>
    setToggled((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });

  return (
    <div className="space-y-3 rounded-md border border-border-default bg-bg-muted-rd p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[13px] font-medium text-text-primary">
          Revise os feriados de {year} antes de gravar
        </p>
        <p className="text-[11px] text-text-tertiary">
          {chosen.length} de {novos.length} selecionados
        </p>
      </div>

      {novos.length === 0 ? (
        <p className="text-[12px] text-text-secondary">
          Todas as datas de {year} já estão no calendário — nada a carregar.
        </p>
      ) : (
        <ul className="space-y-1">
          {holidays.map((h) => {
            const presente = alreadyPresent.has(h.date);
            const marcado = !presente && isChecked(h);
            return (
              <li key={h.date} className="flex items-center gap-2 text-[12px]">
                <input
                  type="checkbox"
                  id={`holiday-${h.date}`}
                  className="accent-rd-primary-500 h-3.5 w-3.5"
                  checked={marcado}
                  disabled={presente || busy}
                  onChange={() => toggle(h.date)}
                />
                <label
                  htmlFor={`holiday-${h.date}`}
                  className={presente ? "text-text-tertiary" : "text-text-primary"}
                >
                  <span className="font-mono">{fmt.format(new Date(`${h.date}T12:00:00Z`))}</span>{" "}
                  {h.name}
                </label>
                {presente ? (
                  <span className="text-[11px] text-text-tertiary">já cadastrado</span>
                ) : null}
                {h.suggestion ? (
                  <span className="text-[11px] text-text-tertiary">
                    sugestão — não é feriado nacional
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          disabled={busy || chosen.length === 0}
          onClick={() => onConfirm(chosen)}
        >
          <Check className="mr-1.5 h-4 w-4" aria-hidden /> Gravar {chosen.length}{" "}
          {chosen.length === 1 ? "data" : "datas"}
        </Button>
        <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={onCancel}>
          <X className="mr-1.5 h-4 w-4" aria-hidden /> Cancelar
        </Button>
      </div>
    </div>
  );
}
